import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { Box, IconButton, MenuItem, TextField } from "@mui/material";
import {
  AdditionalFormFieldProps,
  BaseFormFieldDefinition,
} from "@pimo/pimo-components/src/lib/pimo-components/pimo-form/form-field";
import { useState } from "react";
import { useController, useFormContext } from "react-hook-form";

export const REPEATABLE_INPUT_FIELD_TYPE = "repeatableInput" as const;

const ENVIRONMENTS = [
  "Production",
  "UAT",
  "SIT",
  "Development",
  "Other",
] as const;

export type RepeatableInputFieldDefinition = BaseFormFieldDefinition<
  typeof REPEATABLE_INPUT_FIELD_TYPE
> & {
  /** Pairs each text input with an environment dropdown. */
  withEnvironment?: boolean;
};

type Row = { environment: string; text: string };

const FIELD_HEIGHT = {
  "& .MuiInputBase-root": { height: 45 },
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    "& .MuiOutlinedInput-notchedOutline": { borderRadius: "8px" },
  },
};

// shortcut: a pair is persisted as "<environment>: <url>" so FormData stays string[]; upgrade if rows need structured values.
const encode = ({ environment, text }: Row, withEnvironment: boolean) =>
  withEnvironment ? `${environment}: ${text}` : text;

const decode = (value: string, withEnvironment: boolean): Row => {
  if (!withEnvironment) return { environment: "", text: value };
  const environment = ENVIRONMENTS.find((env) => value.startsWith(`${env}: `));
  // Answers saved before the pair UI existed are plain URLs.
  return environment
    ? { environment, text: value.slice(environment.length + 2) }
    : { environment: "Other", text: value };
};

const toValues = (raw: unknown): string[] =>
  Array.isArray(raw) ? raw : typeof raw === "string" && raw ? [raw] : [];

const nextEnvironment = (rows: Row[]): string =>
  ENVIRONMENTS.find((env) => !rows.some((row) => row.environment === env)) ??
  "Other";

export function RepeatableInputFormField({
  disabled,
  errorMessage,
  name,
  required,
  sx,
  withEnvironment = false,
}: RepeatableInputFieldDefinition & AdditionalFormFieldProps) {
  const { control } = useFormContext();
  const {
    field: { value, onChange, onBlur },
  } = useController({
    control,
    name,
    rules: {
      validate: (v) =>
        !required?.value || toValues(v).length > 0 || required.message,
    },
  });

  const blankRow = (rows: Row[]): Row => ({
    environment: withEnvironment ? nextEnvironment(rows) : "",
    text: "",
  });
  const fromValue = (raw: unknown): Row[] => {
    const rows = toValues(raw).map((v) => decode(v, withEnvironment));
    return rows.length > 0 ? rows : [blankRow([])];
  };

  const [rows, setRows] = useState<Row[]>(() => fromValue(value));
  const committed = (next: Row[]) =>
    next.filter((r) => r.text.trim()).map((r) => encode(r, withEnvironment));

  // Rows the user has added but not filled are not part of the value, so only an
  // outside change (reload, reset after save) may replace them.
  const [prevValue, setPrevValue] = useState(value);
  if (prevValue !== value) {
    setPrevValue(value);
    if (JSON.stringify(toValues(value)) !== JSON.stringify(committed(rows))) {
      setRows(fromValue(value));
    }
  }

  const update = (next: Row[]) => {
    setRows(next);
    onChange(committed(next));
  };
  const patch = (index: number, change: Partial<Row>) =>
    update(rows.map((row, i) => (i === index ? { ...row, ...change } : row)));

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      {rows.map((row, index) => (
        <Box key={index} sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          {withEnvironment && (
            <TextField
              select
              disabled={disabled}
              value={row.environment}
              onChange={(e) => patch(index, { environment: e.target.value })}
              sx={{ ...sx, ...FIELD_HEIGHT, minWidth: 160 }}
              slotProps={{ htmlInput: { "aria-label": "Environment" } }}
            >
              {ENVIRONMENTS.map((env) => (
                <MenuItem key={env} value={env}>
                  {env}
                </MenuItem>
              ))}
            </TextField>
          )}
          <TextField
            fullWidth
            disabled={disabled}
            error={!!errorMessage}
            value={row.text}
            placeholder={withEnvironment ? "https://" : undefined}
            onChange={(e) => patch(index, { text: e.target.value })}
            onBlur={onBlur}
            sx={{ ...sx, ...FIELD_HEIGHT }}
          />
          {!disabled && (
            <>
              <IconButton
                aria-label={`Remove row ${index + 1}`}
                onClick={() => {
                  const remaining = rows.filter((_, i) => i !== index);
                  update(remaining.length ? remaining : [blankRow([])]);
                }}
              >
                <RemoveIcon />
              </IconButton>
              <IconButton
                aria-label="Add row"
                onClick={() => setRows([...rows, blankRow(rows)])}
              >
                <AddIcon />
              </IconButton>
            </>
          )}
        </Box>
      ))}
      {errorMessage && (
        <Box sx={{ color: "error.main", fontSize: 12 }}>{errorMessage}</Box>
      )}
    </Box>
  );
}
